import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, Overline } from "@/components/Reveal";
import { PageHero, Section, Button } from "@/components/UI";
import { Slogan } from "@/components/Slogan";
import { IMG } from "@/lib/images";

const VALUES = [
  { t: "Public Protection", d: "Our first duty is to the people who receive restorative care. Standards exist to keep them safe." },
  { t: "Clinical Rigour", d: "We treat micropigmentation as a health discipline governed by evidence, not trends." },
  { t: "Dignity & Empathy", d: "Trauma-informed care is not optional; it is woven through every standard we set." },
  { t: "National Consistency", d: "One standard, coast to coast, so care does not depend on where a person lives." },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  return (
    <>
      <header className="relative border-b border-ccpm-border bg-ccpm-stone/50" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-40 pb-20">
          <motion.img initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            src="/ccpm-logo.png" alt="Canadian Council for Paramedical Micropigmentation" className="h-20 lg:h-24 w-auto mb-10" />
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="overline text-ccpm-olive mb-6">About CCPM</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ccpm-charcoal max-w-4xl">A national body for a discipline that deserves one</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.28 }}
            className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-ccpm-taupe font-light">CCPM is being developed as Canada's certifying body, standards leader, and educational home for restorative paramedical micropigmentation — clearly distinct from the cosmetic permanent makeup industry.</motion.p>
        </div>
      </header>

      <Section className="py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <Overline>Mission</Overline>
            <h2 className="font-serif text-3xl lg:text-4xl text-ccpm-charcoal mt-4 leading-tight">Protecting the public and elevating the profession.</h2>
            <p className="mt-6 text-ccpm-taupe font-light leading-relaxed">We establish and uphold a national standard for restorative paramedical micropigmentation, so that people healing from cancer, surgery, trauma, congenital conditions, and skin disorders can access safe, dignified, competent care.</p>
            <Overline className="mt-12 block">Vision</Overline>
            <h2 className="font-serif text-3xl lg:text-4xl text-ccpm-charcoal mt-4 leading-tight">Restorative micropigmentation recognized as clinical care.</h2>
            <p className="mt-6 text-ccpm-taupe font-light leading-relaxed">A Canada where restorative micropigmentation is understood, regulated, and integrated into the healthcare journey — not confused with cosmetic beauty services.</p>
          </Reveal>
          <div ref={ref} className="relative">
            <motion.div style={{ y }} className="overflow-hidden aspect-[4/5]">
              <img src={IMG.empathy} alt="A CCPM practitioner performing restorative micropigmentation" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </Section>

      <div className="bg-ccpm-stone py-24 lg:py-32">
        <Section>
          <Reveal><Overline>Our Values</Overline></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mt-10">
            {VALUES.map((v, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <span className="font-serif text-4xl text-ccpm-olive/40">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-2xl text-ccpm-charcoal mt-3">{v.t}</h3>
                <p className="mt-3 text-sm text-ccpm-taupe font-light leading-relaxed">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* BRAND SLOGAN */}
      <Slogan variant="olive" testId="about-slogan"
        quote="Beyond the pigment is a person."
        emphasis="Beyond the technique is a responsibility." />

      <Section className="py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4"><Reveal><Overline>Governance</Overline><h2 className="font-serif text-4xl text-ccpm-charcoal mt-4 leading-tight">Led by clinicians, survivors, and educators.</h2></Reveal></div>
          <div className="lg:col-span-8 space-y-8">
            {[
              ["Board of Directors", "Provides strategic direction and fiduciary oversight, composed of practitioners, healthcare professionals, and public members."],
              ["Standards & Ethics Committee", "Develops the standards of practice and code of ethics, and reviews public complaints under a confidential procedure."],
              ["Education & Certification Committee", "Sets certification requirements, approves curriculum, and oversees continuing competency."],
              ["Advisory Council", "Includes oncology, plastic surgery, dermatology, and survivor advocates to keep the standard clinically grounded."],
            ].map(([t, d], i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="border-t border-ccpm-border pt-6">
                  <h3 className="font-serif text-2xl text-ccpm-charcoal">{t}</h3>
                  <p className="mt-2 text-ccpm-taupe font-light leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section className="pb-32"><Reveal><div className="border border-ccpm-border p-10 lg:p-14 text-center">
        <h2 className="font-serif text-3xl lg:text-4xl text-ccpm-charcoal">Help shape the national standard</h2>
        <p className="mt-4 text-ccpm-taupe font-light max-w-xl mx-auto">We welcome practitioners, clinicians, and members of the public to contribute during our consultation period.</p>
        <Button to="/contact" className="mt-8" data-testid="about-cta">Get involved</Button>
      </div></Reveal></Section>
    </>
  );
}
